import React, {
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import moment from "moment";
import {
  SimpleDialog,
  Button,
  SimpleForm,
  SimpleUtility,
} from "simple-ui-components";
import validator from "validator";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { put } from "../../server-requests/serverRequests";
import { DateTimeStyled } from "../../theme";
import { getTranslation } from "../languageProvider";
import {
  DateFormatTimezone,
  DateTimeFormatTimezone,
  Log,
  ReplaceWord,
  TimeFormatTimezone,
} from "../shared/utility";
import { AuthContext } from "../authContextProvider";
import DatePickerHolder from "../shared/datePickerHolder";

const validateRoutes = ({ name }) => {
  let error = "";
  if (!name) {
    error = {
      message:
        getTranslation("0113") &&
        ReplaceWord(getTranslation("0113"), "**NAME**", getTranslation("0132")),
      errorOn: "name",
    };
  } else {
    if (!validator.isLength(name, { min: 1, max: 30 })) {
      error = {
        message:
          getTranslation("0119") &&
          ReplaceWord(
            getTranslation("0119"),
            "**NAME**",
            getTranslation("0132"),
            "**MIN**",
            1,
            "**MAX**",
            30
          ),
        errorOn: "name",
      };
    }
  }
  return error;
};

export const EditRoutes = forwardRef(({ UpdateSuccess }, ref) => {
  const routeNameRef = useRef();
  const observationsRef = useRef();

  const { Dialog, DialogBody, DialogFooter } = SimpleDialog;
  const { InputGroup } = SimpleForm;
  const { UpdateObject } = SimpleUtility;

  const { snackbarHandler } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [toEdit, setToEdit] = useState({});

  const [startedAt, setStartedAt] = useState(false);
  const [endedAt, setEndedAt] = useState(false);

  const [startedDate, setStartedDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [startedTime, setStartedTime] = useState(
    moment(new Date()).format("HH:mm:ss")
  );
  const [startedDateTime, setStartedDateTime] = useState(
    moment(new Date()).format("YYYY-MM-DD HH:mm:ss")
  );

  const [endedDate, setEndedDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [endedTime, setEndedTime] = useState(
    moment(new Date()).format("HH:mm:ss")
  );
  const [endedDateTime, setEndedDateTime] = useState(
    moment(new Date()).format("YYYY-MM-DD HH:mm:ss")
  );

  const [getError, setError] = useState({
    name: false,
  });

  const handleClose = useCallback(() => setOpen(false), []);

  // trigger from props
  useImperativeHandle(ref, () => ({
    RoutesHandler(clickedRoutes) {
      setOpen(true);
      setToEdit(clickedRoutes);
      setError((prev) => UpdateObject(prev, { name: false }));
      setStartedAt(false);
      setEndedAt(false);
    },
  }));

  const clickAddEditHandler = () => {
    let toSend = {
      name: routeNameRef.current.value,
      observations: observationsRef.current.value,
    };
    let error = validateRoutes(toSend);
    if (error !== "") {
      snackbarHandler(error.message);
      setError({
        [error.errorOn]: true,
      });
    } else {
      if (startedAt) toSend.started = startedDateTime;
      if (endedAt) toSend.ended = endedDateTime;

      put("routes", toSend, {
        id: toEdit.id,
      })
        .then(() => {
          snackbarHandler(
            getTranslation("0115") &&
              ReplaceWord(
                getTranslation("0115"),
                "**NAME**",
                getTranslation("0008")
              ),
            "success"
          );
          setOpen(false);
          UpdateSuccess();
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: update routes" + status);
        });
    }
  };

  // started at
  const changeStartedAtHandler = (event) => setStartedAt(event);
  const changeEndedAtHandler = (event) => setEndedAt(event);

  const startedDateChangeHandler = (value) => {
    let selectedDate = moment(value).format("YYYY-MM-DD");
    let fullDateTime = selectedDate + " " + startedTime;
    setStartedDate(selectedDate);
    setStartedDateTime(moment(fullDateTime).format("YYYY-MM-DD HH:mm:ss"));
  };

  const startedTimeChangeHandler = (value) => {
    let selectedTime = moment(value).format("HH:mm:ss");
    let fullDateTime = startedDate + " " + selectedTime;
    setStartedTime(selectedTime);
    setStartedDateTime(moment(fullDateTime).format("YYYY-MM-DD HH:mm:ss"));
  };

  const endedDateChangeHandler = (value) => {
    let selectedDate = moment(value).format("YYYY-MM-DD");
    let fullDateTime = selectedDate + " " + endedTime;
    setEndedDate(selectedDate);
    setEndedDateTime(moment(fullDateTime).format("YYYY-MM-DD HH:mm:ss"));
  };

  const endedTimeChangeHandler = (value) => {
    let selectedTime = moment(value).format("HH:mm:ss");
    let fullDateTime = endedDate + " " + selectedTime;
    setEndedTime(selectedTime);
    setEndedDateTime(moment(fullDateTime).format("YYYY-MM-DD HH:mm:ss"));
  };

  let startedDisplay = startedAt || toEdit.started;
  let endedDisplay = endedAt || toEdit.ended;

  if (!open) return null;
  return (
    <Dialog
      theme="default"
      size="sm"
      close={handleClose}
      open={open}
      title={`${getTranslation("0070")} ${getTranslation(
        "0008"
      ).toLowerCase()}`}
    >
      <DialogBody className="outline md">
        <InputGroup
          name="route"
          placeholder={getTranslation("0132")}
          className={getError.name ? "error" : ""}
          ref={routeNameRef}
          inputElement={{
            name: "route",
            type: "text",
            defaultValue: toEdit.name,
            className: "focused",
          }}
        />

        <InputGroup
          type="textarea"
          name="observations"
          placeholder={getTranslation("0016")}
          ref={observationsRef}
          className={getError.observations ? "error" : ""}
          inputElement={{
            defaultValue: toEdit.observations,
            className: toEdit.observations ? "focused" : "",
            rows: 10,
          }}
        />

        {!toEdit.started && (
          <InputGroup
            type="checkbox"
            name="startedAt"
            inputElement={{
              onChange: changeStartedAtHandler,
              defaultValue: startedAt,
              label: getTranslation("0026"),
            }}
          />
        )}

        {startedDisplay && (
          <DateTimeStyled>
            <InputGroup
              type="date"
              name="startedAtDate"
              placeholder={getTranslation("0160")}
              className={toEdit.started ? "disabled" : ""}
            >
              <DatePickerHolder
                elements={{
                  type: "date",
                  onChange: startedDateChangeHandler,
                  name: "startedAtDate",
                  value: toEdit.started
                    ? DateTimeFormatTimezone(toEdit.started)
                    : startedDateTime,
                  disabled: toEdit.started,
                }}
              />
            </InputGroup>

            <InputGroup
              type="date"
              name="startedAtTime"
              placeholder={getTranslation("0161")}
              className={toEdit.started ? "disabled" : ""}
            >
              <DatePickerHolder
                elements={{
                  type: "time",
                  onChange: startedTimeChangeHandler,
                  name: "startedAtTime",
                  value: toEdit.started
                    ? DateTimeFormatTimezone(toEdit.started)
                    : startedDateTime,
                  disabled: toEdit.started,
                }}
              />
            </InputGroup>
          </DateTimeStyled>
        )}

        {!toEdit.ended && (
          <InputGroup
            type="checkbox"
            name="endedAt"
            inputElement={{
              onChange: changeEndedAtHandler,
              defaultValue: endedAt,
              label: getTranslation("0027"),
            }}
          />
        )}
        {endedDisplay && (
          <DateTimeStyled>
            <InputGroup
              type="date"
              name="endedAtDate"
              placeholder={getTranslation("0162")}
              className={toEdit.ended ? "disabled" : ""}
            >
              <DatePickerHolder
                elements={{
                  type: "date",
                  onChange: endedDateChangeHandler,
                  name: "endedAtDate",
                  value: toEdit.ended
                    ? DateTimeFormatTimezone(toEdit.ended)
                    : endedDateTime,
                  disabled: toEdit.ended,
                }}
              />
            </InputGroup>
            <InputGroup
              type="date"
              name="endedAtTime"
              placeholder={getTranslation("0163")}
              className={toEdit.ended ? "disabled" : ""}
            >
              <DatePickerHolder
                elements={{
                  type: "time",
                  onChange: endedTimeChangeHandler,
                  name: "endedAtTime",
                  value: toEdit.ended
                    ? DateTimeFormatTimezone(toEdit.ended)
                    : endedDateTime,
                  disabled: toEdit.ended,
                }}
              />
            </InputGroup>
          </DateTimeStyled>
        )}
      </DialogBody>
      <DialogFooter multiple={true}>
        <Button
          type="button"
          clicked={clickAddEditHandler}
          icon={<SaveOutlinedIcon />}
          text={getTranslation("0024")}
          className="btn-primary"
          theme="default"
          style={{ marginLeft: "auto" }}
        />
      </DialogFooter>
    </Dialog>
  );
});
